export declare type ConsumerFn<T> = (data: T) => void;

export declare type NoArgVoidFn = () => void;

export interface Promotion {
  promotionCode: string;
  campaign: string;
  promotionName: string;
  promotionDescription: string;
  termsAndConditions: string;
  webImage: string;
  webThumb: string;
  mobileImage: string;
  mobileThumb: string;
  expirationDate: string;
  isValid: boolean;
  invalidReason: string | null;
  isReusable: boolean;
  expiringSoon: boolean;
  promotionId: number;
  promotionActivity: PromotionActivity | null;
  promotionGroup: string | null;
}

export interface PromotionActivity {
  activityType: string;
  // TODO Tech Debt this doesn't appear to be used anywhere
  activityDate: string;
  storeName: string;
  storeNumber: string;
  storeAddress: string;
}

export interface CreditOrDebitCard {
  [key: string]: string | CreditCardType | undefined;
  accountHolderName: string;
  accountNumber: string;
  expirationDate: string;
  cvv: string;
  zipCode: string;
  type?: CreditCardType;
}

export interface TokenizedPaymentCard {
  tokenId?: number;
  tokenizedAccountNumber: string;
  expiration?: string;
  cardHolderName: string;
  paymentMethod: string;
  billingAddress?: string;
  billingCity?: string;
  billingStateProvince?: string;
  billingZip?: string;
  billingCountry?: string;
  isOneTimePaymentCard?: boolean;
  isGiftCard?: boolean;
  expirationMonth?: string;
  expirationYear?: string;
  recaptchaToken?: string;
  paymentProviderId?: string;
  lastFourAccountNumbers?: string;
  transactionId?: string;
  loyaltyAutoAccrual?: LoyaltyAutoAccrual | null;
}

export interface LoyaltyAutoAccrual {
  eligible: boolean;
  enrolled: boolean;
}

export interface PayeezyTokenizedPaymentResponse {
  results: {
    token: {
      cardholder_name: string;
      exp_date: string;
      type: string;
      value: string;
    };
    status: string;
  };
  status: number;
}

export interface PayeezyError {
  results: {
    Error: {
      messages: {
        code: string;
        description: string;
      }[];
    };
  };
}

export enum CreditCardType {
  Visa = 'visa',
  Mastercard = 'mastercard',
  Amex = 'american express',
  Discover = 'discover',
}

export enum PromotionsApiErrorCode {
  PromoNotFound = 21,
}

export interface PromotionsApiError {
  errorCode: PromotionsApiErrorCode;
  errorDescription: string;
}

export interface PaymentProvider {
  paymentProviderId: string;
  default?: boolean;
  clientToken: string;
  paymentToken: string;
  supportedPaymentTypes?: PaymentType[];
}

export interface PaymentType {
  name: string;
  type: string;
}

export interface ISessionAuth {
  clientToken: string;
  publicKeyBase64: string;
}

export class SessionAuth implements ISessionAuth {
  public clientToken: string;
  public publicKeyBase64: string;

  constructor(clientToken: string, publicKeyBase64: string) {
    this.clientToken = clientToken;
    this.publicKeyBase64 = publicKeyBase64;
  }
}

export interface IPaymentFormHooks {
  preFlowHook: ConsumerFn<ConsumerFn<ISessionAuth>>;
}

interface IResult {
  error: boolean;
}

export interface ISuccessResult extends IResult {
  error: false;
}

export interface IFailureResult extends IResult {
  reason: string;
  error: true;
}

export interface ITokenizeSuccess extends ISuccessResult {
  clientToken: string;
}

export declare type TokenizeResult = ITokenizeSuccess | IFailureResult;

export declare enum FieldName {
  Name = 'name',
  Card = 'card',
  Cvv = 'cvv',
  Expiration = 'exp'
}

export interface IFieldValidity<F extends FieldName = FieldName> {
  empty: boolean;
  field: F;
  length: number;
  potentiallyValid: boolean;
  touched: boolean;
  valid: boolean;
}

export declare type FormValidity = {
  [F in FieldName]: IFieldValidity<F>;
} & {
  card: ICardFieldValidity;
};

export interface ICardFieldValidity extends IFieldValidity<FieldName.Card> {
  brand: string;
  brandNiceType: string;
}

export interface ICssClassList {
  empty: string;
  focus: string;
  invalid: string;
  valid: string;
}

export interface ICssStyle {
  [index: string]: string | ICssStyle;
}

export interface ICssStyleList {
  [index: string]: ICssStyle;
}

export interface IField {
  selector: string;
  placeholder?: string;
  validity?: FormValidationResultData;
}

export interface IFields {
  card: IField;
  cvv?: IField;
  exp: IField;
  postalCode: IField;
  name: IField;
}

export interface IStateConfig {
  form?: {
    selector: string;
  };
  fields: IFields;
  classes?: ICssClassList;
  styles?: ICssStyleList;
}

export interface IPaymentForm {
  state: IStateConfig;
  tokenize(callback: ConsumerFn<TokenizeResult>): void;
  onSubmit(resolve: ConsumerFn<string>, reject?: ConsumerFn<Error>): void;
  createFields(resolve: NoArgVoidFn, reject?: ConsumerFn<Error>): void;
  reset(resolve: NoArgVoidFn, reject?: ConsumerFn<Error>): void;
  destroyFields(resolve: NoArgVoidFn, reject?: ConsumerFn<Error>): void;
  validate(resolve: NoArgVoidFn, reject?: ConsumerFn<Error>): void;
  authenticate(resolve: ConsumerFn<ISessionAuth>, reject?: ConsumerFn<Error>): void;
  submit(auth: ISessionAuth, resolve: ConsumerFn<string>, reject?: ConsumerFn<Error>): void;
  isSupported(): boolean;
  isValid(): boolean;
  getState(cb: ConsumerFn<FormValidity>): void;
}

export class FormValidationResultData {
  public brand?: string;
  public brandNiceType?: string;
  public empty: boolean = true;
  public field?: string;
  public length?: number;
  public potentiallyValid?: boolean;
  public touched?: boolean;
  public valid: boolean = true;
  public selector?: string;
}

export class FormValidationResult {
  public static valid = (): FormValidationResult => new FormValidationResult(false, 'Validation is complete');
  public static error = (message: string, data: FormValidationResultData[]): FormValidationResult => new FormValidationResult(true, message, data);

  public error: boolean = false;
  public message?: string;
  public data?: FormValidationResultData[];

  private constructor(error: boolean, message: string, data: FormValidationResultData[] = []) {
    this.error = error;
    this.message = message;
    this.data = data;
  }
}

export interface GiftCardBalanceResponse {
  currentBalance: number;
}

export interface IPaymentTokenResponse {
  isSuccess?: boolean;
  paymentToken?: string;
  paymentMethod?: string;
  cardHolderName?: string;
  expiration?: string;
  errorReason?: string;
  last4?: string;
  billingZip?: string;
}

export class PaymentTokenResponse implements IPaymentTokenResponse {

  static fromJS(data: any): PaymentTokenResponse {
    data = typeof data === 'object' ? data : {};
    const result = new PaymentTokenResponse();
    if (!data.isSuccess) {
      return result;
    }
    result.init(data);
    return result;
  }

  isSuccess?: boolean;
  paymentToken?: string;
  authTransactionId?: string;
  paymentMethod?: string;
  cardHolderName?: string;
  expiration?: string;
  errorReason?: string;
  last4?: string;
  billingZip?: string;

  constructor(data?: IPaymentTokenResponse) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) {
          (<any>this)[property] = (<any>data)[property];
        }
      }
    }
  }

  init(data?: any) {
    if (data) {
      this.isSuccess = data.isSuccess;
      this.paymentToken = data.paymentToken;
      this.authTransactionId = data.authTransactionId;
      this.errorReason = data.errorReason;
      this.paymentMethod = data.card.brand;
      this.cardHolderName = data.card.name;
      this.expiration = data.card.exp.month + data.card.exp.year.slice(-2);
      this.last4 = data.card.last4;
      this.billingZip = data.card.billingZip;
    }
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data.isSuccess = this.isSuccess;
    data.paymentToken = this.paymentToken;
    data.authTransactionId = this.authTransactionId;
    data.errorReason = this.errorReason;
    data.cardHolderName = this.cardHolderName;
    data.paymentMethod = this.paymentMethod;
    data.expiration = this.expiration;
    data.last4 = this.last4;
    data.billingZip = this.billingZip;
    return data;
  }
}

export interface IInitializeResponse {
  clientToken?: string;
  paymentToken?: string;
  supportedPaymentTypes?: string[];
}

export class InitializeResponse implements IInitializeResponse {

  static fromJS(data: any): InitializeResponse {
    data = typeof data === 'object' ? data : {};
    const result = new InitializeResponse();
    result.init(data);
    return result;
  }

  clientToken?: string;
  paymentToken?: string;
  authTransactionId?: string;
  sessionId?: string;
  uId?: string;
  ucom?: InitializeResponseUCOM;
  checkoutId?: string;
  supportedPaymentTypes?: string[];

  constructor(data?: IInitializeResponse) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) {
          (<any>this)[property] = (<any>data)[property];
        }
      }
    }
  }

  init(data?: any) {
    if (data) {
      this.clientToken = data['clientToken'];
      this.paymentToken = data['paymentToken'];
      this.authTransactionId = data['authTransactionId'];
      this.sessionId = data['sessionId'];
      this.uId = data['uId'];
      if (Array.isArray(data['supportedPaymentTypes'])) {
        this.supportedPaymentTypes = [] as any;
        for (const item of data['supportedPaymentTypes']) {
          this.supportedPaymentTypes!.push(item);
        }
      }
      this.initUCOM(data["ucom"]);
      this.initChaseUk(data["chasUK"]);
    }
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['clientToken'] = this.clientToken;
    data['paymentToken'] = this.paymentToken;
    data['sessionId'] = this.sessionId;
    data['uId'] = this.uId;
    if (Array.isArray(this.supportedPaymentTypes)) {
      data['supportedPaymentTypes'] = [];
      for (const item of this.supportedPaymentTypes) {
        data['supportedPaymentTypes'].push(item);
      }
    }
    return data;
  }
  
  initUCOM(data?: any) {
    if(data) {
      const guestCheckoutLink = (data["pageLinks"].links as Array<any>).find(i => i.rel === 'account.chipotle.guest.checkout');

      const ucom: InitializeResponseUCOM = {
        tokenID: data["tokenId"],
        apiKey: data["apiKey"],
        pageUrl: guestCheckoutLink.href,
        encryptionKey: data["encryptionKey"],
        redirectUrl: data["redirectUrl"]
      };
      this.ucom = ucom;
    }
  }

  initChaseUk(data?: any){
    if(data) {
      this.checkoutId = data['checkoutId'];
    }
  }
}

export interface InitializeResponseUCOM {
  tokenID?: string;
  apiKey?: string;
  pageUrl?: string;
  encryptionKey?: string;
  redirectUrl?: string;
}

export interface IBaseExceptionResponse {
  message?: string;
  category?: string;
  errorCode?: string;
  statusCode?: number;
  correlationId?: string;
}

export class BaseExceptionResponse implements IBaseExceptionResponse {

  static fromJS(data: any): BaseExceptionResponse {
    data = typeof data === 'object' ? data : {};
    const result = new BaseExceptionResponse();
    result.init(data);
    return result;
  }

  message?: string;
  category?: string;
  errorCode?: string;
  statusCode?: number;
  correlationId?: string;

  constructor(data?: IBaseExceptionResponse) {
    if (data) {
      for (const property in data) {
        if (data.hasOwnProperty(property)) {
          (<any>this)[property] = (<any>data)[property];
        }
      }
    }
  }

  init(data?: any) {
    if (data) {
      this.message = data['message'];
      this.category = data['category'];
      this.errorCode = data['errorCode'];
      this.statusCode = data['statusCode'];
      this.correlationId = data['correlationId'];
    }
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['message'] = this.message;
    data['category'] = this.category;
    data['errorCode'] = this.errorCode;
    data['statusCode'] = this.statusCode;
    data['correlationId'] = this.correlationId;
    return data;
  }
}